import React, { useState, useEffect, useCallback } from 'react';
import { crossSourceApi } from '../api';

const CONFIDENCE_CONFIG = {
  HIGH:   { label: 'Hoch',   color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  MEDIUM: { label: 'Mittel', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  LOW:    { label: 'Niedrig',color: '#64748b', bg: 'rgba(100,116,139,0.12)' },
};

const SIGNAL_LABEL = {
  P1_VERTRAGSNUMMER:      'Vertragsnummer',
  P2_KUNDENNUMMER_ANBIETER: 'Kundennummer + Anbieter',
  P3_MANDATSREFERENZ:     'Mandatsreferenz',
  P4_ANBIETER_BETRAG:     'Anbieter + Betrag',
};

function fmtEur(cents) {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function Badge({ confidence }) {
  const cfg = CONFIDENCE_CONFIG[confidence] || CONFIDENCE_CONFIG.LOW;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 99,
      background: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 12,
    }}>
      {cfg.label}
    </span>
  );
}

function ConflictBadge({ conflicts }) {
  const keys = Object.keys(conflicts || {});
  if (!keys.length) return null;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 99,
      background: 'rgba(248,113,113,0.12)', color: '#f87171', fontWeight: 600, fontSize: 12, marginLeft: 6,
    }}>
      ⚠ {keys.length} Konflikt{keys.length > 1 ? 'e' : ''}
    </span>
  );
}

function LinkCard({ link, onDismiss, onOpen }) {
  const conflicts = JSON.parse(link.conflicts_json || '{}');
  const reasons = JSON.parse(link.match_reasons_json || '[]');
  const signalLabel = SIGNAL_LABEL[link.match_signal] || link.match_signal;

  return (
    <div
      style={{
        background: '#1e293b', borderRadius: 12, padding: '16px 20px',
        marginBottom: 12, border: '1px solid #334155', cursor: 'pointer',
      }}
      onClick={() => onOpen(link.id)}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <Badge confidence={link.match_confidence} />
            <ConflictBadge conflicts={conflicts} />
            <span style={{ color: '#94a3b8', fontSize: 12 }}>
              Signal: {signalLabel}
            </span>
          </div>
          <div style={{ fontWeight: 700, color: '#f1f5f9', fontSize: 15, marginBottom: 4 }}>
            {link.bcc_title || link.bcc_counterparty || '(unbekannt)'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginTop: 8 }}>
            <SourceBlock
              icon="🏦"
              label="Kontoauszug"
              title={link.bcc_title}
              subtitle={fmtEur(link.bcc_amount_cents)}
              status={link.bcc_status}
              url={link.bank_candidate_id ? `/kontoauszuege` : null}
            />
            <SourceBlock
              icon="📄"
              label="Paperless"
              title={link.psr_title}
              subtitle={link.psr_result_type?.replace('_', ' ')}
              status={link.psr_status}
              url={link.psr_url}
              external
            />
          </div>
          {link.contract_titel && (
            <div style={{ marginTop: 8, color: '#6366f1', fontSize: 12 }}>
              Verknüpfter Vertrag: {link.contract_titel}
            </div>
          )}
          {reasons.length > 0 && (
            <div style={{ marginTop: 6, color: '#64748b', fontSize: 12 }}>
              {reasons.join(' · ')}
            </div>
          )}
        </div>
        <button
          onClick={e => { e.stopPropagation(); onDismiss(link.id); }}
          className="btn-secondary"
          style={{ flexShrink: 0 }}
        >
          Verwerfen
        </button>
      </div>
    </div>
  );
}

function SourceBlock({ icon, label, title, subtitle, status, url, external }) {
  return (
    <div style={{
      background: '#0f172a', borderRadius: 8, padding: '8px 12px',
      border: '1px solid #1e293b',
    }}>
      <div style={{ color: '#64748b', fontSize: 11, marginBottom: 2 }}>
        {icon} {label}
      </div>
      <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {title || '—'}
      </div>
      <div style={{ color: '#94a3b8', fontSize: 12 }}>{subtitle || '—'}</div>
      {status && (
        <div style={{ marginTop: 2, fontSize: 11, color: status === 'PENDING' ? '#f59e0b' : '#64748b' }}>
          {status}
        </div>
      )}
    </div>
  );
}

function DetailModal({ linkId, onClose }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    crossSourceApi.link(linkId).then(setLink).finally(() => setLoading(false));
  }, [linkId]);

  if (loading) return (
    <Overlay onClose={onClose}>
      <div style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>Laden…</div>
    </Overlay>
  );
  if (!link) return null;

  const conflicts = JSON.parse(link.conflicts_json || '{}');
  const reasons = JSON.parse(link.match_reasons_json || '[]');
  const bccFields = JSON.parse(link.bcc_proposed_fields_json || '{}');
  const psrFields = JSON.parse(link.psr_proposed_fields_json || '{}');
  const psrProv   = JSON.parse(link.psr_provenance_json || '{}');

  return (
    <Overlay onClose={onClose}>
      <div style={{ maxWidth: 680, width: '100%', background: '#1e293b', borderRadius: 16, padding: 28, overflow: 'auto', maxHeight: '90vh' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 700, color: '#f1f5f9', fontSize: 18, marginBottom: 4 }}>
              {link.bcc_title || link.bcc_counterparty}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Badge confidence={link.match_confidence} />
              <span style={{ color: '#64748b', fontSize: 12 }}>
                {SIGNAL_LABEL[link.match_signal] || link.match_signal}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost">✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          {/* Bank Card */}
          <div style={{ background: '#0f172a', borderRadius: 10, padding: 16, border: '1px solid #1e293b' }}>
            <div style={{ color: '#6366f1', fontWeight: 700, fontSize: 13, marginBottom: 10 }}>🏦 Kontoauszug (FAKT)</div>
            <Field label="Anbieter" value={link.bcc_counterparty} />
            <Field label="Betrag" value={fmtEur(link.bcc_amount_cents)} />
            <Field label="Intervall" value={link.bcc_interval} />
            <Field label="Vertragsnr." value={link.bcc_vertragsnummer} />
            <Field label="Kundennr." value={link.bcc_kundennummer} />
            <Field label="Mandatsref." value={link.bcc_mandatsreferenz} />
            <Field label="Buchungen" value={link.bcc_transaction_count} />
            <Field label="Konfidenz" value={link.bcc_confidence_label} />
            {link.bcc_status !== 'PENDING' && (
              <div style={{ marginTop: 8, color: '#f59e0b', fontSize: 11 }}>Status: {link.bcc_status}</div>
            )}
          </div>

          {/* Paperless Card */}
          <div style={{ background: '#0f172a', borderRadius: 10, padding: 16, border: '1px solid #1e293b' }}>
            <div style={{ color: '#22c55e', fontWeight: 700, fontSize: 13, marginBottom: 10 }}>📄 Paperless (INDIKATOR)</div>
            <Field label="Titel" value={link.psr_title} />
            <Field label="Dokumenttyp" value={link.psr_document_type} />
            <Field label="Match-Score" value={link.psr_match_score != null ? `${link.psr_match_score}` : null} />
            {Object.entries(psrFields).map(([k, v]) => (
              <Field key={k} label={k} value={typeof v === 'object' ? v?.wert : v} badge={psrProv[k]?.quelle} />
            ))}
            {link.psr_url && (
              <a href={link.psr_url} target="_blank" rel="noopener noreferrer"
                style={{ color: '#6366f1', fontSize: 12, display: 'block', marginTop: 8 }}>
                In Paperless öffnen ↗
              </a>
            )}
          </div>
        </div>

        {/* Conflicts */}
        {Object.keys(conflicts).length > 0 && (
          <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ color: '#f87171', fontWeight: 700, fontSize: 13, marginBottom: 8 }}>⚠ Abweichende Werte</div>
            {Object.entries(conflicts).map(([field, c]) => (
              <div key={field} style={{ fontSize: 13, marginBottom: 6, color: '#f1f5f9' }}>
                <span style={{ color: '#94a3b8' }}>{field}:</span>
                {' '}Bank ({c.bank_quelle}) = <strong>{c.bank_wert}</strong>
                {' '}vs. Paperless ({c.paperless_quelle}) = <strong>{c.paperless_wert}</strong>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  Bankbetrag ist FAKT — maßgeblich bei Widerspruch.
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Reasons */}
        {reasons.length > 0 && (
          <div style={{ color: '#64748b', fontSize: 12, marginBottom: 16 }}>
            Signale: {reasons.join(' · ')}
          </div>
        )}

        {/* Guidance */}
        <div style={{ background: '#0f172a', borderRadius: 10, padding: 14, color: '#94a3b8', fontSize: 12, marginBottom: 16 }}>
          <strong style={{ color: '#f1f5f9' }}>Nächste Schritte:</strong>
          <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
            <li>Kandidat im <a href="/kontoauszuege" style={{ color: '#6366f1' }}>Kontoauszug-Center</a> bestätigen oder ignorieren.</li>
            {link.psr_url && <li>Paperless-Dokument unter "In Paperless öffnen" einsehen.</li>}
            <li>Felder werden nie automatisch übernommen — immer manuell prüfen.</li>
          </ul>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn-secondary">Schließen</button>
        </div>
      </div>
    </Overlay>
  );
}

function Field({ label, value, badge }) {
  if (value == null || value === '') return null;
  return (
    <div style={{ marginBottom: 6, fontSize: 13 }}>
      <span style={{ color: '#64748b' }}>{label}: </span>
      <span style={{ color: '#f1f5f9' }}>{String(value)}</span>
      {badge && <span style={{ marginLeft: 6, color: '#94a3b8', fontSize: 11 }}>({badge})</span>}
    </div>
  );
}

function Overlay({ onClose, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 720 }}>
        {children}
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { key: 'PENDING', label: 'Ausstehend' },
  { key: 'DISMISSED', label: 'Verworfen' },
];

export default function CrossSourcePage() {
  const [tab, setTab]         = useState('PENDING');
  const [links, setLinks]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [openId, setOpenId]   = useState(null);
  const [error, setError]     = useState(null);

  const load = useCallback(async (status = tab) => {
    setLoading(true);
    setError(null);
    try {
      const [res, sum] = await Promise.all([
        crossSourceApi.links({ status }),
        crossSourceApi.summary(),
      ]);
      setLinks(res.links || []);
      setTotal(res.total || 0);
      setSummary(sum);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(tab); }, [tab]);

  async function handleRun() {
    setRunning(true);
    setRunResult(null);
    setError(null);
    try {
      const result = await crossSourceApi.run();
      setRunResult(result);
      await load(tab);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setRunning(false);
    }
  }

  async function handleDismiss(id) {
    try {
      await crossSourceApi.dismiss(id);
      setLinks(prev => prev.filter(l => l.id !== id));
      setSummary(prev => prev ? { ...prev, pending_total: Math.max(0, prev.pending_total - 1) } : prev);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  }

  return (
    <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ color: '#f1f5f9', fontSize: 22, fontWeight: 800, margin: 0 }}>
            Vertragsabgleich
          </h1>
          <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>
            Bank-Kandidaten und Paperless-Dokumente, die denselben Vertrag beschreiben.
          </p>
        </div>
        <button
          onClick={handleRun}
          disabled={running}
          className="btn-primary"
          style={{ cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.7 : 1 }}
        >
          {running ? '⏳ Analyse läuft…' : '🔍 Abgleich starten'}
        </button>
      </div>

      {/* Summary Banner */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
          <SummaryCard label="Ausstehend" value={summary.pending_total} color="#f59e0b" />
          <SummaryCard label="Hoch" value={summary.pending_high} color="#22c55e" />
          <SummaryCard label="Mittel" value={summary.pending_medium} color="#f59e0b" />
        </div>
      )}

      {runResult && (
        <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13, color: '#a5b4fc' }}>
          Abgleich abgeschlossen: {runResult.links_created_or_updated} neue Treffer aus {runResult.bank_candidates} Bank-Kandidaten × {runResult.paperless_results} Paperless-Ergebnissen.
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(248,113,113,0.1)', borderRadius: 10, padding: 12, marginBottom: 16, color: '#f87171', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #334155' }}>
        {STATUS_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              background: 'none', border: 'none', color: tab === t.key ? '#6366f1' : '#64748b',
              fontWeight: tab === t.key ? 700 : 400, fontSize: 14, padding: '8px 16px',
              cursor: 'pointer', borderBottom: tab === t.key ? '2px solid #6366f1' : '2px solid transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: '#64748b', textAlign: 'center', padding: 40 }}>Laden…</div>
      ) : links.length === 0 ? (
        <div style={{ color: '#64748b', textAlign: 'center', padding: 40 }}>
          {tab === 'PENDING' ? 'Keine ausstehenden Treffer. Abgleich starten oder Kontoauszüge/Paperless-Scan ausführen.' : 'Keine verworfenen Treffer.'}
        </div>
      ) : (
        <div>
          <div style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>{total} Treffer</div>
          {links.map(link => (
            <LinkCard
              key={link.id}
              link={link}
              onDismiss={handleDismiss}
              onOpen={setOpenId}
            />
          ))}
        </div>
      )}

      {openId && (
        <DetailModal linkId={openId} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 16px', border: '1px solid #334155' }}>
      <div style={{ color, fontSize: 22, fontWeight: 800 }}>{value}</div>
      <div style={{ color: '#64748b', fontSize: 12 }}>{label}</div>
    </div>
  );
}
